<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('master.tooltipCommon.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <span class="tti-viewTitle">
            <!-- エリア別分析 -->
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.analysisByAreaTitle') }}
            <!-- グラフ表示(エリア別) -->
            <span class="tti-viewTitle-chartarea" v-if="tableRouteQuery.level === areaRole.AREA">
              <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
              <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
              {{ $t('operationalPerformance.showGraphArea') }}
            </span>
            <!-- グラフ表示(都道府県別) -->
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
            {{ $t('operationalPerformance.showGraphPrefecture') }}
            <!-- 管理車両分析 -->
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            <span
              >{{ $t('operationalPerformance.managementObjectAnalysis', [managementTargetShowName])
              }}{{ `（${this.tableRouteQuery.label}）` }}</span
            >
          </span>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('operationalPerformance.contentTitle', [managementTargetShowName]) }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <div class="tableContainer">
        <el-table
          :data="visableTableData"
          height="calc(100vh - 226px)"
          :empty-text="$t('messages.common.emptyTableData')"
          border
          header-row-class-name="tti-master-table-header"
          :row-class-name="tableRowClassName"
        >
          <!-- 管理対象名 -->
          <el-table-column
            prop="managementTargetName"
            :label="$t('operationalPerformance.managementObjectName', [managementTargetShowName])"
            width="150"
            ><template slot-scope="scope">
              <div :id="'tableData[' + scope.$index + '].managementTargetName'">
                {{ scope.row.managementTargetName }}
              </div></template
            ></el-table-column
          >
          <!-- 稼働率 -->
          <el-table-column
            prop="operatingRate"
            :label="$t('operationalPerformance.operatingRate')"
            width="100"
            align="right"
          ></el-table-column>
          <!-- 稼働日数 -->
          <el-table-column
            prop="operatingDayNumber"
            :label="$t('operationalPerformance.operatingDays')"
            width="100"
            align="right"
          ></el-table-column>
          <!-- 期間日数 -->
          <el-table-column
            prop="dateInterval"
            :label="$t('operationalPerformance.periodDays')"
            width="100"
            align="right"
          ></el-table-column>
          <!-- 稼働詳細 -->
          <template v-for="(operating, index) in tableData[0] ? tableData[0].historyList : []">
            <el-table-column width="100" :key="index" :label="operating.operatingDate">
              <template slot-scope="scope">
                <!-- 都道府県内 -->
                <span v-if="isResultWithinTarget(tableData[scope.$index].historyList[index].isOutside)">
                  {{ tableData[scope.$index].historyList[index].timeCountHhmm }}
                </span>
                <!-- 都道府県外 -->
                <span
                  v-else
                  class="outsideHistory"
                  :class="getHistoryStyleByLocal(tableData[scope.$index].historyList[index].isOutside)"
                  @click="showHistoryDetail(tableData[scope.$index].historyList[index].outsideHistoryList)"
                >
                  {{ tableData[scope.$index].historyList[index].timeCountHhmm }}
                </span>
              </template>
            </el-table-column>
          </template>
          <!-- 稼働時間 -->
          <el-table-column
            prop="timeCountTotal"
            :label="$t('operationalPerformance.operatingDate')"
            width="100"
          ></el-table-column>
        </el-table>
      </div>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 印刷 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onHistoryPdfDownloadClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.printing')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="he_008"></tti-svg-icon>
          {{ $t('buttons.print') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onHistoryCsvDownloadClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.outPut')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>
          {{ $t('buttons.output') }}
        </el-button>
      </div>
    </section>

    <!-- 画面で選択した都道府県以外の実績データ表示画面 -->
    <ttiDialog
      :isShow="showDialog"
      @handelDisplay="onHandelDisplay"
      :showHelp="false"
      :title="$t('operationalPerformance.resultThatOutOfPrefecture')"
      width="500px"
    >
      <template slot="body">
        <el-table
          :data="historyDetail"
          :empty-text="$t('messages.common.emptyTableData')"
          border
          header-row-class-name="tti-master-table-header"
          height="400"
        >
          <!-- 都道府県名 -->
          <el-table-column
            prop="prefectureName"
            :label="$t('operationalPerformance.prefectureName')"
            width="310"
          ></el-table-column>
          <!-- 稼働時間 -->
          <el-table-column prop="operatingDate" :label="$t('operationalPerformance.operatingDate')" width="150">
            <template slot-scope="scope">
              <span :class="getHistoryStyleByLocal(scope.row.isOutside)">
                {{ scope.row.timeCountHhmm }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'

import { i18n } from '@/locales'
import TtiDialog from '@/components/TtiDialog.vue'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import { localService } from '@/commons/services/localService'
import { AreaRole, DeleteType } from '@/commons/constants'
import {
  ResponseData,
  TableRouteQuery,
  OutsideOperatingDetail,
  SearchTargets,
  ManagementObjectAnalysisData
} from '@/models'
import { getManagementObjectAnalysisData, opPerfPrefectureCSVDownload, opPerfAreaManagePDFDownload } from '@/store/apis'
import { changeBlobToJson } from '@/store/apis/apiClient'
import config from '@/config/config'
import { correctTimeFormat, isDateAfter, isDateDayOver } from '@/utils/dateUtils'
import { ManagementTargetHistoryType } from '@/commons/constants'
import { FormItemType } from '@/views/master/MST_BASE.vue'

/**
 * グラフ表示画面
 *
 * @class
 */
@Component({
  name: 'op-perf-trgts',
  components: { TtiDialog }
})
export default class CHART_PREFECTURE extends Vue {
  areaRole = AreaRole
  /**
   * グラフルーティングパラメータ定義
   */
  tableRouteQuery: TableRouteQuery = {
    level: '',
    label: '',
    prefectureId: '',
    operationStartDay: '',
    operationEndDay: '',
    deleteFlg: ''
  }

  /**
   * 管理対象分析データ
   */
  tableData: ManagementObjectAnalysisData[] = []

  /**
   * 当前表示データ
   */
  visableTableData: ManagementObjectAnalysisData[] = []

  /**
   * ページの表示件数
   */
  visablePageSize = 50

  /**
   * 当前ページ
   */
  currentPage = 1

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'managementTargetName'

  /**
   * 検索文字列
   */
  oldSearchText = ''

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 検索目標
   */
  searchTargets: SearchTargets[] = []

  /**
   * 検索結果INDEX
   */
  searchResultIndexList: number[] = []

  /**
   * 検索結果のPoint
   */
  searchResultPoint = -1

  /**
   * 検索結果INDEX
   */
  searchResultIndex = -1

  /**
   * 管理対象表示名
   *
   * @returns {string} 管理対象表示名
   */
  get managementTargetShowName(): string {
    return localService.userInfo?.managementTargetShowName
  }

  /**
   * 画面で選択した都道府県以外の実績データ
   */
  historyDetail: OutsideOperatingDetail[] = []

  /**
   * ダイアログ表示フラグ
   */
  showDialog = false

  /**
   * ダイアログ表示状態変更
   *
   * @returns {void}
   */
  onHandelDisplay(): void {
    this.showDialog = !this.showDialog
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('OP_PERF_TRGTS > mounted')
    this.initTable()
    if (!this.routeQueryCheck()) {
      this.movepage()
      return
    }
    this.areaNotExistProxy(this.handleShowGraph)
    this.initSearchTargets()
  }

  /**
   * URLから管理对象分析取得
   *
   * @returns {void}
   */
  initTable(): void {
    this.tableRouteQuery = {
      level: this.$route.query.level as string,
      label: this.$route.query.label as string,
      prefectureId: this.$route.query.prefectureId as string,
      operationStartDay: this.$route.query.operationStartDate as string,
      operationEndDay: this.$route.query.operationEndDate as string,
      deleteFlg: this.$route.query.deleteFlg as string
    }
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {ManagementObjectAnalysisData[]} data 検索されたデータ
   * @returns {void}
   */
  initTableData(data: ManagementObjectAnalysisData[]): void {
    if (!data) {
      data = []
    }
    this.initVisableData()
    this.initSearchBox()
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @returns {void}
   */
  initVisableData(): void {
    this.currentPage = 1
    this.oldSearchText = ''
    this.searchText = ''
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
    this.setVisableTableData()
  }

  /**
   * 検索ボックスの初期化を実行する。
   *
   * @returns {void}
   */
  initSearchBox(): void {
    this.oldSearchText = ''
    this.searchText = ''
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {number} currentPage 当前ページ
   * @returns {void}
   */
  setVisableTableData(currentPage = 1): void {
    if (!this.tableData) {
      return
    }

    const startIndex = (currentPage - 1) * this.visablePageSize
    const endIndex = currentPage * this.visablePageSize

    this.visableTableData = this.tableData.slice(startIndex, endIndex)
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} パラメータ着信がない場合
   */
  routeQueryCheck(): boolean {
    if (this.tableRouteQuery.level !== AreaRole.AREA && this.tableRouteQuery.level !== AreaRole.PREFECTURE) {
      return false
    }
    if (
      this.tableRouteQuery.deleteFlg !== DeleteType.notdeleteData &&
      this.tableRouteQuery.deleteFlg !== DeleteType.deleteData
    ) {
      return false
    }
    if (
      !this.tableRouteQuery.prefectureId ||
      this.tableRouteQuery.prefectureId.length === 0 ||
      !this.tableRouteQuery.operationStartDay ||
      !this.tableRouteQuery.operationEndDay ||
      !this.tableRouteQuery.deleteFlg
    ) {
      return false
    }

    if (
      !correctTimeFormat(this.tableRouteQuery.operationStartDay) ||
      !correctTimeFormat(this.tableRouteQuery.operationEndDay) ||
      isDateAfter(this.tableRouteQuery.operationStartDay, this.tableRouteQuery.operationEndDay) ||
      isDateDayOver(this.tableRouteQuery.operationEndDay, this.tableRouteQuery.operationStartDay, 31)
    ) {
      return false
    }

    return true
  }

  /**
   * パラメータが間違っている場合の処理
   */
  movepage(): void {
    msgBoxService.info(i18n.t('vaildate.common.E00092').toString())
    this.$router.push(config.menu.areaAnalysis)
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onBackButtonClick(): Promise<void> {
    window.history.back()
  }

  /**
   * APIを呼び出すときに、エリアが存在しない可能性があります、これらのAPIをプロキシする。
   *
   * @param {any} target プロキシの目標処理
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line
  async areaNotExistProxy(target: any): Promise<void> {
    try {
      await target()
    } catch (e) {
      this.$log.debug('OP_PERF_TRGTS > areaNotExistProxy: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、エリア別分析画面へ遷移する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.$router.push(config.menu.areaAnalysis)
        }
      })
      throw e
    }
  }

  /**
   * 管理対象分析画面の処理
   *
   * @returns {void}
   */
  async handleShowGraph(): Promise<void> {
    const params = {
      prefectureId: this.tableRouteQuery.prefectureId,
      operationStartDate: this.tableRouteQuery.operationStartDay,
      operationEndDate: this.tableRouteQuery.operationEndDay,
      deleteFlg: this.tableRouteQuery.deleteFlg
    }
    const managementObjectAnalysisDataResponse = await getManagementObjectAnalysisData(params)
    this.tableData = managementObjectAnalysisDataResponse.managementTargetList || []
    this.initTableData(this.tableData)
    this.tableData.map((item, index) => {
      if (item.operatingRate || item.operatingRate === 0) {
        this.tableData[index].operatingRate = parseInt((item.operatingRate * 100).toFixed(1))
      }
    })
  }

  /**
   * 管理対象の実績が利用可能エリア外で発生するかどうかの判断
   *
   * @param {string} isOutsideFlg 利用可能エリア外かどうかのフラグ
   * @returns {boolean} 判断結果
   */
  isResultWithinTarget(isOutsideFlg: string): boolean {
    return isOutsideFlg === ManagementTargetHistoryType.IN_TARGET
  }

  /**
   * 利用可能エリア外データのCSSを取得する
   *
   * @param {string} isOutsideFlg 利用可能エリア外かどうかのフラグ
   * @returns {Record<string, boolean>} 判断結果によって返却するCSS
   */
  getHistoryStyleByLocal(isOutsideFlg: string): Record<string, boolean> {
    if (isOutsideFlg === ManagementTargetHistoryType.OUTSIDE_BUT_IN_ALLOWED_AREA) {
      return { 'history-local-in-allowed': true }
    } else {
      return { 'history-local-out-of-allowed': true }
    }
  }

  /**
   * 画面で選択した都道府県以外の実績データを表示する
   *
   * @param {OutsideOperatingDetail[]} historyList 画面で選択した都道府県以外の実績データ
   */
  showHistoryDetail(historyList: OutsideOperatingDetail[]): void {
    this.historyDetail = historyList || []
    if (this.historyDetail.length === 0) {
      return
    }

    this.showDialog = true
  }

  /**
   * 管理対象分析CSV出力API
   *
   * @returns {void}
   */
  async managementTableCsvOutputAPI(): Promise<void> {
    const params = {
      prefectureId: this.tableRouteQuery.prefectureId,
      operationStartDate: this.tableRouteQuery.operationStartDay,
      operationEndDate: this.tableRouteQuery.operationEndDay,
      deleteFlg: this.tableRouteQuery.deleteFlg
    }
    const res = await opPerfPrefectureCSVDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 管理対象分析PDF出力API
   *
   * @returns {void}
   */
  async managementTablePdfOutputAPI(): Promise<void> {
    const params = {
      areaIdList: [this.tableRouteQuery.prefectureId],
      operationStartDate: this.tableRouteQuery.operationStartDay,
      operationEndDate: this.tableRouteQuery.operationEndDay,
      areaLevel: 1,
      deleteFlg: this.tableRouteQuery.deleteFlg
    }
    const res = await opPerfAreaManagePDFDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  onHistoryPdfDownloadClick(): void {
    const confirmCallback = () => {
      this.managementTablePdfOutputAPI()
    }

    msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
  }

  onHistoryCsvDownloadClick(): void {
    const confirmCallback = () => {
      this.managementTableCsvOutputAPI()
    }

    msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'managementTargetName'
      },
      {
        type: FormItemType.Input,
        field: 'operatingRate'
      },
      {
        type: FormItemType.Input,
        field: 'operatingDayNumber'
      },
      {
        type: FormItemType.Input,
        field: 'dateInterval'
      },
      {
        type: FormItemType.Input,
        field: 'timeCountTotal'
      },
      {
        type: FormItemType.Select,
        field: 'timeCountHhmm',
        getData: (item: ManagementObjectAnalysisData): string => {
          if (!item.historyList || item.historyList.length === 0) {
            return ''
          }

          let result: string[] = []

          item.historyList.forEach(item => {
            result.push(item.timeCountHhmm)

            if (!item.timeCountHhmm) {
              return
            }
          })

          return result.join(',')
        }
      }
    ]
  }

  /**
   * 当前ページを変更するときの処理
   *
   * @param {number} page 当前ページ
   * @returns {void}
   */
  onCurrentPageChange(page: number): void {
    this.setVisableTableData(page)
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.searchText = this.searchText.replace(/[,]/g, '')

    if (!this.searchText) {
      this.initSearchBox()
      return
    }

    if (this.oldSearchText === this.searchText) {
      this.doSearchBoxHighlight()
      return
    }

    // 命中したデータリストを作成する
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
    this.oldSearchText = ''
    this.searchTargetIndexByTextBox(this.tableData)
    if (this.searchResultIndexList.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noSearchBoxResult').toString())
      return
    }

    this.oldSearchText = this.searchText
    this.doSearchBoxHighlight()
  }

  /**
   * 検索結果のHighlight
   *
   * @returns {void}
   */
  doSearchBoxHighlight(): void {
    this.toNextSearchResultPoint()
    this.handleSearchResultHighlight()
    this.scrollToHighlightRow()
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @param {Array<any>} targetTableData 対象データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchTargetIndexByTextBox(targetTableData: Array<any>): void {
    targetTableData.forEach((item, index) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const target: any[] = []
      this.searchTargets.forEach(searchTargets => {
        const { type, field, list, getData } = searchTargets
        if (type === FormItemType.Input) {
          target.push(item[field])
        } else if (list) {
          const generalList = list()
          const format = {
            code: 'code',
            value: 'name'
          }
          target.push(HelperService.getDefinitionName(generalList, item[field], format))
        } else if (getData) {
          target.push(getData(item))
        }
      })

      const itemContext = target.toString()

      if (itemContext.includes(this.searchText)) {
        this.searchResultIndexList.push(index)
      }
    })
  }

  /**
   * 次の検索結果へ
   *
   * @returns {void}
   */
  toNextSearchResultPoint(): void {
    const targetsLength = this.searchResultIndexList.length
    if (this.searchResultPoint === targetsLength - 1) {
      this.searchResultPoint = 0
    } else {
      this.searchResultPoint++
    }
  }

  /**
   * 検索結果のページと位置を確定
   *
   * @returns {void}
   */
  handleSearchResultHighlight(): void {
    if (this.searchResultIndexList.length === 0) {
      return
    }

    const targetindex = this.searchResultIndexList[this.searchResultPoint]
    // 目標のページを算出する
    this.searchResultIndex = targetindex % this.visablePageSize
    const targetPage = Math.ceil((targetindex + 1) / this.visablePageSize) || 1
    if (this.currentPage !== targetPage) {
      this.currentPage = targetPage
      this.onCurrentPageChange(this.currentPage)
    }
    this.$log.debug(this.searchResultIndex)
  }

  scrollToHighlightRow(): void {
    this.$nextTick(() => {
      const targetId = `tableData[${this.searchResultIndex}].${this.firstField}`
      const target = document.getElementById(targetId)
      target?.scrollIntoView()
    })
  }

  /**
   * テーブル行CLASS（検索用、rewriteの場合、必ずこの機能を流用してください。）
   *
   * @param {any} rowData 対象データ
   * @returns {string} 目標CLASS
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  tableRowClassName(rowData: any): string {
    const { rowIndex } = rowData
    if (this.searchResultIndex === -1) {
      return ''
    }

    if (this.searchResultIndex === rowIndex) {
      return 'search-result-row'
    }

    return ''
  }
}
</script>

<style lang="scss" scoped>
.tti-dialog-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #909399;
}

.tti-content-title {
  font-size: $FS02;
}

.tti-viewTitle-chartarea {
  display: flex;
  align-items: center;
}

.history-local-in-allowed {
  color: $C31;
}

.history-local-out-of-allowed {
  color: $C22;
}

.outsideHistory {
  cursor: pointer;
}
</style>
